import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarClock, Check, X, LogIn, LogOut, MapPin, AlertTriangle } from "lucide-react";
import { asArray, ALLERGY_SEVERITY_STYLES, getAllergySeverity } from "@/lib/clientFields";
import { Link } from "react-router-dom";
import CareFlagsPills from "@/components/CareFlagsPills";
import MobileMyShiftsList from "@/components/mobile/MobileMyShiftsList";

const dayLabel = (iso) => {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
};

const timeLabel = (iso) => new Date(iso).toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" });

function StatusBadge({ v }) {
  if (v.acceptance_status === "accepted") {
    return <span data-testid={`ms-accepted-${v.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-emerald-50 border-emerald-300 text-emerald-800">Accepted</span>;
  }
  if (v.acceptance_status === "declined") {
    return <span data-testid={`ms-declined-${v.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-red-50 border-red-300 text-red-800">Declined</span>;
  }
  if (v.published_at) {
    return <span data-testid={`ms-pending-${v.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-amber-50 border-amber-300 text-amber-800">Awaiting accept</span>;
  }
  return <span data-testid={`ms-scheduled-${v.id}`} className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 border bg-slate-100 border-slate-300 text-slate-700">Scheduled</span>;
}

export default function MyShifts() {
  const [shifts, setShifts] = useState([]);
  const [safety, setSafety] = useState({});
  // Batch 4.2 — cached `/sites` for mobile Site/House lookup. Care workers
  // already have GET access to /sites; no permission or backend change.
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, sf, st] = await Promise.all([
        api.get("/rota/my-shifts"),
        api.get("/dashboard/client-safety").catch(() => ({ data: { clients: {} } })),
        api.get("/sites").catch(() => ({ data: [] })),
      ]);
      setShifts(r.data || []);
      setSafety(sf.data?.clients || {});
      setSites(st.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // site_id → name lookup for the mobile view (Site/House chip on cards).
  const siteById = useMemo(() => {
    const map = {};
    sites.forEach((s) => { if (s?.id) map[s.id] = s.name; });
    return map;
  }, [sites]);

  const act = async (id, kind) => {
    if (kind === "decline" && !window.confirm("Decline this shift? Your manager will be notified.")) return;
    setBusyId(id);
    try {
      await api.post(`/visits/${id}/${kind}`);
      toast.success(kind === "accept" ? "Shift accepted" : "Shift declined — manager notified");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  const check = async (id, kind) => {
    setBusyId(id);
    try {
      await api.post(`/visits/${id}/${kind}`);
      toast.success(kind === "check-in" ? "Checked in" : "Checked out");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusyId(null); }
  };

  // group shifts by day
  const byDay = {};
  shifts.forEach((v) => {
    const key = (v.scheduled_start || "").slice(0, 10);
    if (!byDay[key]) byDay[key] = [];
    byDay[key].push(v);
  });
  const dayKeys = Object.keys(byDay).sort();
  const pendingCount = shifts.filter((v) => v.published_at && (!v.acceptance_status || v.acceptance_status === "pending")).length;

  return (
    <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
      {/* -----------------------------------------------------------------
          Batch 4.3 · MOBILE / TABLET view ( <lg ) — lifecycle actions
          (Accept / Decline / Check-in / Check-out) reuse the SAME `act`
          and `check` handlers desktop uses — same endpoints, same
          request bodies, same server-side attendance/payroll writes.
          --------------------------------------------------------------- */}
      <MobileMyShiftsList
        loading={loading}
        shifts={shifts}
        byDay={byDay}
        dayKeys={dayKeys}
        siteById={siteById}
        safety={safety}
        pendingCount={pendingCount}
        canAct={true}
        busyId={busyId}
        onAct={act}
        onCheck={check}
      />

      {/* Desktop header ( ≥ lg ). Hidden below lg — MobilePageHeader owns
          the top bar there. Desktop markup is unchanged from before
          Batch 4.2. */}
      <div className="h-14 border-b border-slate-200 bg-white px-6 hidden lg:flex items-center justify-between shrink-0">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-slate-500 flex items-center gap-1.5">
            <CalendarClock className="w-3 h-3 text-sky-700" /> Care delivery
          </div>
          <h1 className="font-heading text-xl font-semibold" data-testid="ms-title">My shifts</h1>
        </div>
        {pendingCount > 0 && (
          <span className="text-xs text-amber-800 bg-amber-50 border border-amber-300 px-2 py-1" data-testid="ms-pending-count">
            {pendingCount} shift{pendingCount === 1 ? "" : "s"} awaiting your acceptance
          </span>
        )}
      </div>

      <div className="hidden lg:block flex-1 overflow-y-auto p-6 space-y-5">
        {loading ? (
          <div className="text-sm text-slate-500" data-testid="ms-loading">Loading your shifts…</div>
        ) : shifts.length === 0 ? (
          <div className="bg-white border border-slate-200 p-8 text-center text-sm text-slate-500" data-testid="ms-empty">
            No upcoming shifts assigned to you yet. When your manager publishes the rota you&apos;ll see everything here.
          </div>
        ) : (
          dayKeys.map((day) => (
            <section key={day} data-testid={`ms-day-${day}`}>
              <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold mb-2">{dayLabel(day)}</div>
              <ul className="space-y-2">
                {byDay[day].map((v) => (
                  <li key={v.id} data-testid={`ms-visit-${v.id}`} className="bg-white border border-slate-200 p-4 flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <div className="font-medium text-slate-900">{v.client_name || v.client_id}</div>
                        <StatusBadge v={v} />
                        {asArray(v.client_allergies).slice(0, 2).map((a) => (
                          <span key={a} className={`inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] rounded-full border ${ALLERGY_SEVERITY_STYLES[getAllergySeverity(a)]}`}>
                            <AlertTriangle className="w-3 h-3" /> {a}
                          </span>
                        ))}
                        {asArray(v.client_allergies).length > 2 && (
                          <span className="text-[11px] text-slate-500">+{asArray(v.client_allergies).length - 2}</span>
                        )}
                      </div>
                      {(safety[v.client_id]?.critical_allergies?.length > 0) && (
                        <div className="mt-1 text-[11px] text-red-800 bg-red-50 border-l-2 border-red-500 px-2 py-1" data-testid={`ms-critical-allergy-${v.id}`}>
                          <AlertTriangle className="w-3 h-3 inline-block mr-1" />
                          {safety[v.client_id].critical_allergies.map((a, i) => (
                            <span key={i}>{i > 0 ? " · " : ""}<span className="font-semibold uppercase text-[10px] tracking-widest">{a.severity}</span> {a.item}{a.reaction ? ` — ${a.reaction}` : ""}</span>
                          ))}
                        </div>
                      )}
                      {(safety[v.client_id]?.care_flags?.length > 0) && (
                        <div className="mt-1"><CareFlagsPills flags={safety[v.client_id].care_flags} size="sm" /></div>
                      )}
                      <div className="text-xs text-slate-600 mt-1">
                        {timeLabel(v.scheduled_start)}{v.scheduled_end ? ` → ${timeLabel(v.scheduled_end)}` : ""}
                        {v.purpose ? ` · ${v.purpose}` : ""}
                      </div>
                      {v.notes && <div className="text-xs text-slate-500 mt-1 flex items-center gap-1"><MapPin className="w-3 h-3" /> {v.notes}</div>}
                      {v.published_at && (
                        <div className="text-[11px] text-slate-500 mt-1">
                          Published {new Date(v.published_at).toLocaleDateString()} · by {v.published_by || "manager"}
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {v.published_at && (!v.acceptance_status || v.acceptance_status === "pending") && (
                        <>
                          <Button size="sm" onClick={() => act(v.id, "accept")} disabled={busyId === v.id} data-testid={`btn-ms-accept-${v.id}`} className="h-9 bg-emerald-700 hover:bg-emerald-800">
                            <Check className="w-3.5 h-3.5 mr-1.5" /> Accept
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => act(v.id, "decline")} disabled={busyId === v.id} data-testid={`btn-ms-decline-${v.id}`} className="h-9 border-red-300 text-red-700">
                            <X className="w-3.5 h-3.5 mr-1.5" /> Decline
                          </Button>
                        </>
                      )}
                      {v.acceptance_status === "accepted" && v.status !== "completed" && !v.check_in_at && (
                        <Button size="sm" variant="outline" onClick={() => check(v.id, "check-in")} disabled={busyId === v.id} data-testid={`btn-ms-checkin-${v.id}`} className="h-9">
                          <LogIn className="w-3.5 h-3.5 mr-1.5" /> Check in
                        </Button>
                      )}
                      {v.check_in_at && v.status !== "completed" && (
                        <Button size="sm" onClick={() => check(v.id, "check-out")} disabled={busyId === v.id} data-testid={`btn-ms-checkout-${v.id}`} className="h-9 bg-slate-900 hover:bg-slate-800">
                          <LogOut className="w-3.5 h-3.5 mr-1.5" /> Check out
                        </Button>
                      )}
                      <Link to={`/clients/${v.client_id}`} className="text-xs text-sky-700 hover:underline" data-testid={`link-ms-client-${v.id}`}>Open profile →</Link>
                    </div>
                  </li>
                ))}
              </ul>
            </section>
          ))
        )}
      </div>
    </div>
  );
}
